import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Wand2, FileText, Brain, Bell, History,
  Copy, Check, RefreshCw, MessageSquare,
  BookOpen, Sparkles, Paperclip, Image as ImageIcon, FileType2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SignupPromptModal } from "@/components/SignupPromptModal";
import { useConfirm } from "@/components/ui/use-confirm";
import { useAuth } from "@/context/AuthContext";
import { useFreeTrial } from "@/hooks/useFreeTrial";
import { useWorkspaceData } from "@/hooks/useWorkspaceData";
import { useBilling } from "@/hooks/useBilling";
import { streamAI } from "@/lib/api";
import {
  TOOL_LABEL, buildPayload, messagesStore,
  conversationSettingsStore, suggestTitleFromMessage,
  formatKnowledgeForPrompt, knowledgeFilledCount,
} from "@/lib/workspaceData";
import { Sidebar } from "@/components/workspace/Sidebar";
import { KnowledgeBaseSheet } from "@/components/workspace/KnowledgeBaseSheet";
import { Composer } from "@/components/workspace/Composer";

const TOOL_ICON = {
  reply_assistant: Wand2,
  proposal_generator: FileText,
  client_analyzer: Brain,
  follow_up: Bell,
  history: History,
};

const TONES = ["professional", "friendly", "confident", "technical", "sales-focused"];
const PLATFORMS = ["upwork", "fiverr", "freelancer", "other"];
const STYLES = ["professional", "friendly", "premium", "sales-focused"];
const LIMIT = 3;

/* ----------------------------- Right Panel ---------------------------- */
function RightPanel({ conv, settings, onSettingChange, lastAnalysis }) {
  if (!conv) return null;

  const Section = ({ title, children }) => (
    <div className="mb-5">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{title}</p>
      <div className="mt-2">{children}</div>
    </div>
  );

  const Chip = ({ active, onClick, label, testId }) => (
    <button data-testid={testId} onClick={onClick}
      className={[
        "rounded-md border px-2.5 py-1 text-[11.5px] capitalize transition-colors",
        active ? "border-primary/50 bg-primary/10 text-foreground" : "border-border bg-background text-muted-foreground hover:text-foreground",
      ].join(" ")}>{label.replace("-", " ")}</button>
  );

  return (
    <aside data-testid="workspace-right-panel" className="hidden w-[300px] shrink-0 border-l border-border bg-card/40 p-5 xl:block">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
        {TOOL_LABEL[conv.tool]}
      </p>

      {conv.tool === "reply_assistant" && (
        <Section title="Tone">
          <div className="flex flex-wrap gap-1.5">
            {TONES.map((t) => (
              <Chip key={t} label={t} active={settings.tone === t}
                testId={`right-tone-${t}`}
                onClick={() => onSettingChange({ tone: t })} />
            ))}
          </div>
        </Section>
      )}

      {conv.tool === "proposal_generator" && (
        <>
          <Section title="Platform">
            <div className="flex flex-wrap gap-1.5">
              {PLATFORMS.map((p) => (
                <Chip key={p} label={p} active={settings.platform === p}
                  testId={`right-platform-${p}`}
                  onClick={() => onSettingChange({ platform: p })} />
              ))}
            </div>
          </Section>
          <Section title="Style">
            <div className="flex flex-wrap gap-1.5">
              {STYLES.map((st) => (
                <Chip key={st} label={st} active={settings.style === st}
                  testId={`right-style-${st}`}
                  onClick={() => onSettingChange({ style: st })} />
              ))}
            </div>
          </Section>
        </>
      )}

      {conv.tool === "client_analyzer" && lastAnalysis && (
        <>
          <Section title="Confidence">
            <p className="font-mono text-2xl text-foreground">
              {lastAnalysis.confidence_score}<span className="text-sm text-muted-foreground">/100</span>
            </p>
          </Section>
          <Section title="Complexity"><p className="text-[13px] text-foreground">{lastAnalysis.complexity}</p></Section>
          {lastAnalysis.red_flags?.length > 0 && (
            <Section title="Red flags">
              <ul className="space-y-1 text-[12px] text-rose-300">{lastAnalysis.red_flags.slice(0, 4).map((r, i) => <li key={i}>· {r}</li>)}</ul>
            </Section>
          )}
          {lastAnalysis.proposal_strategy?.length > 0 && (
            <Section title="Suggested strategy">
              <ul className="space-y-1 text-[12px] text-foreground/85">{lastAnalysis.proposal_strategy.slice(0, 4).map((r, i) => <li key={i}>· {r}</li>)}</ul>
            </Section>
          )}
        </>
      )}

      {conv.tool === "client_analyzer" && !lastAnalysis && (
        <p className="mt-4 text-[12.5px] text-muted-foreground">Send a job description to see win-probability and strategy here.</p>
      )}
    </aside>
  );
}

/* --------------------- Message rendering helpers ---------------------- */
function AnalyzerCard({ data }) {
  return (
    <div className="space-y-2 text-[13px]">
      <p className="font-medium text-foreground">{data.summary}</p>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-md border border-border bg-background p-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Complexity</p>
          <p className="text-[12.5px] text-foreground">{data.complexity}</p>
        </div>
        <div className="rounded-md border border-border bg-background p-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Duration</p>
          <p className="text-[12.5px] text-foreground">{data.estimated_duration || "—"}</p>
        </div>
      </div>
      {data.red_flags?.length > 0 && (
        <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Red flags</p>
          <ul className="mt-1 space-y-0.5">{data.red_flags.map((r, i) => <li key={i} className="text-[12px] text-rose-300">· {r}</li>)}</ul></div>
      )}
      {data.missing_info?.length > 0 && (
        <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Questions to ask</p>
          <ul className="mt-1 space-y-0.5">{data.missing_info.map((r, i) => <li key={i} className="text-[12px] text-foreground/85">· {r}</li>)}</ul></div>
      )}
    </div>
  );
}

function MessageAttachments({ attachments }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mb-2 flex flex-wrap gap-2">
      {attachments.map((a) => {
        const isImage = a.kind === "image" && a.image_b64;
        const Icon = a.kind === "image" ? ImageIcon
          : (a.mime || "").includes("pdf") ? FileType2
          : FileText;
        return (
          <div
            key={a.id || a.name}
            data-testid={`message-attachment-${a.id || a.name}`}
            className="flex items-center gap-2 rounded-lg border border-border bg-background px-2 py-1.5 text-[12px]"
          >
            {isImage ? (
              <img
                src={`data:${a.mime};base64,${a.image_b64}`}
                alt={a.name}
                className="h-7 w-7 shrink-0 rounded object-cover"
              />
            ) : (
              <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded bg-secondary text-muted-foreground">
                <Icon className="h-3.5 w-3.5" />
              </span>
            )}
            <span className="truncate max-w-[200px] text-[12px] text-foreground/90">{a.name}</span>
          </div>
        );
      })}
    </div>
  );
}

function Message({ msg, onRegenerate, canRegenerate }) {
  const [copied, setCopied] = useState(false);
  const isUser = msg.role === "user";
  const isAnalyzer = msg.role === "assistant" && msg.tool === "client_analyzer" && msg.json;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(msg.content);
      setCopied(true); toast.success("Copied");
      setTimeout(() => setCopied(false), 1200);
    } catch { toast.error("Couldn't copy"); }
  };

  return (
    <div data-testid={`message-${msg.role}`} className="px-5 py-5 sm:px-10">
      <div className="mx-auto flex max-w-3xl gap-4">
        <div className={[
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
          isUser ? "bg-secondary text-foreground text-[10.5px] font-semibold" : "bg-primary/15 text-primary",
        ].join(" ")}>
          {isUser ? "You" : <Sparkles className="h-3.5 w-3.5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] leading-relaxed text-foreground/95">
            {isUser && <MessageAttachments attachments={msg.attachments} />}
            {isAnalyzer ? (
              <AnalyzerCard data={msg.json} />
            ) : isUser ? (
              msg.content ? <p className="whitespace-pre-wrap">{msg.content}</p> : null
            ) : (
              <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-headings:mt-3 prose-headings:mb-2 prose-code:rounded prose-code:bg-secondary prose-code:px-1 prose-code:py-0.5 prose-code:text-[12.5px] prose-code:font-normal prose-code:before:content-none prose-code:after:content-none prose-pre:rounded-lg prose-pre:border prose-pre:border-border prose-pre:bg-card prose-a:text-primary prose-strong:text-foreground prose-p:text-foreground prose-li:text-foreground prose-headings:text-foreground">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content || ""}</ReactMarkdown>
                {msg.streaming && (
                  <span data-testid="streaming-cursor"
                    className="ml-0.5 inline-block h-3.5 w-1.5 -mb-0.5 animate-pulse rounded-sm bg-primary align-baseline" />
                )}
              </div>
            )}
            {msg.stopped && (
              <p className="mt-1 text-[11px] italic text-muted-foreground">Stopped by user.</p>
            )}
            {msg.error && (
              <p className="mt-1 text-[11px] italic text-rose-400">{msg.error}</p>
            )}
          </div>
          {!isUser && !msg.streaming && msg.content && (
            <div className="mt-2 flex items-center gap-1.5">
              <button onClick={copy} data-testid="msg-copy-btn"
                className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground">
                {copied ? <Check className="h-3 w-3 text-primary" /> : <Copy className="h-3 w-3" />}
                {copied ? "Copied" : "Copy"}
              </button>
              {canRegenerate && (
                <button onClick={onRegenerate} data-testid="msg-regenerate-btn"
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground">
                  <RefreshCw className="h-3 w-3" /> Regenerate
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Workspace ------------------------------ */
export default function WorkspacePage() {
  const { user } = useAuth();
  const trial = useFreeTrial(LIMIT);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { confirm, ConfirmHost } = useConfirm();
  const billing = useBilling();

  const data = useWorkspaceData();

  const [collapsed, setCollapsed] = useState(false);
  const [activeId, setActiveId] = useState(null);
  // Item #2 — Draft mode: when no conversation is active, we still need a
  // tool selection (default = reply_assistant). The conversation is created
  // only on the FIRST send so empty chats don't pollute Recent Chats.
  const [draftTool, setDraftTool] = useState("reply_assistant");
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState({});
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]);  // Phase 4 — pending uploads
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [kbSheetOpen, setKbSheetOpen] = useState(false);
  const [kbProjectId, setKbProjectId] = useState(null);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);  // AbortController for the active stream

  // Server-side Pro tools (must mirror payments_service.PRO_ONLY_TOOLS).
  // Anonymous users: locked (open signup modal).
  // Starter (authenticated) users: locked (route to /pricing).
  // Pro users: unlocked.
  const PRO_TOOLS = useMemo(() => new Set(["proposal_generator", "client_analyzer"]), []);
  const isLockedForUser = useCallback((tool) => {
    if (!PRO_TOOLS.has(tool)) return false;
    if (!user) return true;            // anon — show signup modal
    if (billing.isPro) return false;   // pro — unlocked
    return true;                       // logged-in starter — show paywall
  }, [user, billing.isPro, PRO_TOOLS]);

  // Bootstrap on first load (after workspace data is hydrated)
  useEffect(() => {
    document.title = "Workspace — MACHA";
  }, []);

  const active = useMemo(
    () => data.conversations.find((c) => c.id === activeId) || null,
    [data.conversations, activeId]
  );

  // Derived: the currently-relevant tool. Either the active conversation's
  // tool, or the draft tool when there's no active conversation.
  const currentTool = active?.tool || draftTool;

  // Phase 3 — Shared Project Memory. The active conversation's project (if any)
  // and its formatted knowledge prompt block. Memoised so we don't reformat on
  // every keystroke.
  const activeProject = useMemo(
    () => (active?.project_id ? data.projects.find((p) => p.id === active.project_id) : null),
    [active?.project_id, data.projects]
  );
  const activeProjectContext = useMemo(
    () => (activeProject ? formatKnowledgeForPrompt(activeProject.knowledge) : ""),
    [activeProject]
  );
  const activeKnowledgeCount = useMemo(
    () => (activeProject ? knowledgeFilledCount(activeProject.knowledge) : 0),
    [activeProject]
  );

  const openKnowledgeFor = useCallback((projectId) => {
    setKbProjectId(projectId);
    setKbSheetOpen(true);
  }, []);
  const kbProject = useMemo(
    () => data.projects.find((p) => p.id === kbProjectId) || null,
    [data.projects, kbProjectId]
  );

  // Hydrate messages + settings whenever the active conversation changes
  useEffect(() => {
    if (!active) { setMessages([]); setSettings({}); return; }
    setMessages(messagesStore.load(active.id));
    setSettings(conversationSettingsStore.load(active.id, active.tool));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, active?.tool]);

  // Once data is loaded, pick an initial state: from URL > or start in draft
  // mode (NO conversation auto-created — Item #2).
  useEffect(() => {
    if (data.loading) return;
    if (activeId) return; // already chosen

    const urlId = params.get("c");
    const urlTool = params.get("tool");

    if (urlId && data.conversations.find((c) => c.id === urlId)) {
      setActiveId(urlId);
      return;
    }
    if (urlTool && !isLockedForUser(urlTool)) {
      // Start a draft for the requested tool (no conversation created yet)
      setDraftTool(urlTool);
      return;
    }
    // Default: stay in draft mode for AI Reply Assistant. Don't auto-create.
  }, [data.loading, isLockedForUser]); // eslint-disable-line

  // Keep URL in sync with active (or with draftTool when no active conv)
  useEffect(() => {
    if (active) {
      setParams({ c: active.id }, { replace: true });
    } else {
      // Draft mode — encode tool but never `c`
      setParams({ tool: draftTool }, { replace: true });
    }
  }, [active?.id, draftTool]); // eslint-disable-line

  // Autoscroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, loading]);

  // ---- Actions ----
  // Item #2: picking a tool puts us in DRAFT mode (no conversation row created).
  // Conversation is created on first successful send inside runGeneration().
  // Item #7: locked tools open the signup modal instead.
  const handlePickTool = (tool) => {
    if (isLockedForUser(tool)) {
      if (!user) { setModalOpen(true); return; }
      // Authenticated Starter user trying to access a Pro tool — route to /pricing.
      toast.info("Upgrade to Pro to unlock this tool.");
      navigate("/pricing");
      return;
    }
    setActiveId(null);
    setDraftTool(tool);
    setMessages([]);
    setInput("");
    setAttachments([]);
  };

  const handleNewChat = () => handlePickTool(currentTool);

  const handlePickConversation = (id) => setActiveId(id);

  // Branded confirm dialogs (replace native window.confirm).
  const confirmAndDeleteConversation = async (id) => {
    const target = data.conversations.find((c) => c.id === id);
    const ok = await confirm({
      title: "Delete this conversation?",
      description: target?.title
        ? `"${target.title}" will be permanently deleted. This cannot be undone.`
        : "This conversation will be permanently deleted. This cannot be undone.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (!ok) return;
    try {
      await data.removeConversation(id);
      if (activeId === id) setActiveId(null);
    } catch (e) { toast.error(e.message || "Couldn't delete"); }
  };

  const confirmAndDeleteProject = async (id) => {
    const proj = data.projects.find((p) => p.id === id);
    const childCount = (data.conversationsByProject[id] || []).length;
    const ok = await confirm({
      title: "Delete this project?",
      description: childCount
        ? `"${proj?.name || "This project"}" will be deleted. Its ${childCount} conversation${childCount === 1 ? "" : "s"} will move to Recent Chats.`
        : `"${proj?.name || "This project"}" will be deleted.`,
      confirmLabel: "Delete project",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (!ok) return;
    try { await data.removeProject(id); }
    catch (e) { toast.error(e.message || "Couldn't delete"); }
  };

  const handleSettingChange = (patch) => {
    if (!active) return;
    const next = { ...settings, ...patch };
    setSettings(next);
    conversationSettingsStore.save(active.id, next);
  };

  const lastAssistantWithJson = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "assistant" && m.json) return m.json;
    }
    return null;
  }, [messages]);

  const runGeneration = async (userText, { regenerateLast = false } = {}) => {
    if (!user && trial.exhausted) { setModalOpen(true); return; }
    if (loading) return; // already streaming — protect against double-send

    // Phase 11: server-side limits mirrored client-side for snappy UX.
    // Pro users are unlimited; Starter is capped at billing.message_limit.
    if (user && !billing.isPro && billing.billing) {
      const used = billing.billing.messages_used || 0;
      const limit = billing.billing.message_limit || 10;
      if (used >= limit) {
        toast.error(`You've used all ${limit} free messages this month.`);
        navigate("/pricing");
        return;
      }
      if (used === limit - 2) {
        toast.warning(`Only 2 free messages left this month.`);
      }
    }

    // Snapshot only successful attachments (no uploading/errored)
    const sendAttachments = attachments.filter((a) => !a._uploading && !a._error);

    // Item #2: lazy-create the conversation only when the user actually sends.
    let conv = active;
    if (!conv) {
      // Defense in depth — block locked tools at send time too
      if (isLockedForUser(draftTool)) { setModalOpen(true); return; }
      try {
        conv = await data.addConversation({ tool: draftTool });
        setActiveId(conv.id);
      } catch (e) {
        toast.error(e.message || "Couldn't start conversation");
        return;
      }
    }

    let next = messages;
    if (regenerateLast) {
      next = next.filter((m, i) => !(i === next.length - 1 && m.role === "assistant"));
    }
    if (userText || sendAttachments.length > 0) {
      const userMsg = {
        id: `m_${Date.now()}`, role: "user",
        content: userText || "",
        attachments: sendAttachments.map((a) => ({
          id: a.id, name: a.name, mime: a.mime, kind: a.kind, size: a.size,
          // Persist a small image thumbnail b64 so it shows on reload; trim text
          image_b64: a.kind === "image" ? a.image_b64 : undefined,
        })),
        ts: Date.now(),
      };
      next = [...next, userMsg];
    }

    // Append an empty assistant message that we'll fill in via stream deltas.
    const aiMsgId = `m_${Date.now() + 1}`;
    const aiMsg = {
      id: aiMsgId, role: "assistant", content: "", json: null,
      tool: conv.tool, ts: Date.now(), streaming: true,
    };
    next = [...next, aiMsg];
    setMessages(next);
    messagesStore.save(conv.id, next);

    // Auto-title from the first user message
    const firstUserMsg = next.find((m) => m.role === "user");
    const suggested = suggestTitleFromMessage(conv.title, firstUserMsg?.content);
    if (suggested) {
      await data.updateConversation(conv.id, suggested);
    } else {
      await data.touch(conv.id);
    }

    const userMsgs = next.filter((m) => m.role === "user");
    const lastUser = userMsgs[userMsgs.length - 1]?.content || "";
    const prevUser = userMsgs.length >= 2 ? userMsgs[userMsgs.length - 2].content : null;

    // Build conversation history (exclude the placeholder assistant + current user msg)
    const HISTORY_LIMIT = 12;
    const historyAll = next.slice(0, -2); // drop current user + placeholder assistant
    const history = historyAll
      .filter((m) => (m.role === "user" || m.role === "assistant") && m.content)
      .slice(-HISTORY_LIMIT)
      .map((m) => ({ role: m.role, content: m.content }));

    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    let accumulated = "";
    let finalAssistant = null;

    await streamAI({
      toolType: conv.tool,
      payload: buildPayload(conv.tool, settings, lastUser, prevUser),
      history,
      sessionId: conv.id,
      projectContext: activeProjectContext || null,
      attachments: sendAttachments.length > 0 ? sendAttachments.map((a) => ({
        id: a.id, name: a.name, mime: a.mime, kind: a.kind, size: a.size,
        extracted_text: a.extracted_text,
        image_b64: a.image_b64,
      })) : null,
      signal: controller.signal,
      onDelta: (chunk) => {
        accumulated += chunk;
        // Update only the streaming assistant message; React renders the new content
        // on each delta so markdown renders progressively.
        setMessages((prev) => {
          const idx = prev.findIndex((m) => m.id === aiMsgId);
          if (idx === -1) return prev;
          const copy = [...prev];
          copy[idx] = { ...copy[idx], content: accumulated };
          return copy;
        });
      },
      onDone: (evt) => {
        let finalContent = evt.content ?? accumulated;
        let json = null;
        if (conv.tool === "client_analyzer") {
          try { json = JSON.parse(finalContent); finalContent = json.summary || finalContent; }
          catch { /* keep raw text */ }
        }
        finalAssistant = {
          id: aiMsgId, role: "assistant", content: finalContent, json,
          tool: conv.tool, ts: Date.now(), streaming: false,
        };
        setMessages((prev) => {
          const idx = prev.findIndex((m) => m.id === aiMsgId);
          if (idx === -1) return prev;
          const copy = [...prev];
          copy[idx] = finalAssistant;
          messagesStore.save(conv.id, copy);
          return copy;
        });
        if (!user) trial.increment();
        else billing.refreshBilling();
      },
      onError: (err) => {
        // Replace the streaming assistant with an error placeholder we can retry
        setMessages((prev) => {
          const idx = prev.findIndex((m) => m.id === aiMsgId);
          if (idx === -1) return prev;
          const copy = [...prev];
          // If we had partial content, keep it but mark error
          copy[idx] = { ...copy[idx], streaming: false, error: err.message || "Generation failed" };
          // If nothing was streamed, drop the empty placeholder altogether
          if (!copy[idx].content) copy.splice(idx, 1);
          messagesStore.save(conv.id, copy);
          return copy;
        });
        // Phase 11: backend returns 402 (limit) / 403 (pro-only) as a stream error
        // before any deltas — the message hints we can detect.
        const m = (err.message || "").toLowerCase();
        if (m.includes("free messages") || m.includes("message_limit") || m.includes("upgrade to pro for unlimited")) {
          toast.error("Free message limit reached. Upgrade to Pro for unlimited.");
          navigate("/pricing");
          return;
        }
        if (m.includes("pro plan only") || m.includes("pro_required")) {
          toast.error("This tool is on the Pro plan. Upgrade to unlock.");
          navigate("/pricing");
          return;
        }
        toast.error(err.message || "Generation failed");
      },
    });

    // If user pressed Stop mid-stream, finalise whatever we have
    if (controller.signal.aborted && !finalAssistant) {
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === aiMsgId);
        if (idx === -1) return prev;
        const copy = [...prev];
        if (!copy[idx].content) {
          copy.splice(idx, 1); // nothing streamed — drop placeholder
        } else {
          copy[idx] = { ...copy[idx], streaming: false, stopped: true };
        }
        messagesStore.save(conv.id, copy);
        return copy;
      });
    }

    abortRef.current = null;
    setLoading(false);
  };

  const onStop = () => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* ignore */ }
    }
  };

  const onSend = () => {
    if (loading) return;
    const text = input.trim();
    const hasAttachments = attachments.some((a) => !a._uploading && !a._error);
    if (!text && !hasAttachments) return;
    setInput("");
    setAttachments([]);
    runGeneration(text);
  };

  const onRegenerate = () => {
    if (!active || loading) return;
    runGeneration(null, { regenerateLast: true });
  };

  const placeholderByTool = {
    reply_assistant: "Paste the client's message — or write your quick reply on the next turn.",
    proposal_generator: "Paste the full job description…",
    client_analyzer: "Paste a job description to analyze…",
  };

  const ToolIcon = TOOL_ICON[currentTool] || MessageSquare;
  const lastAssistantIdx = messages.map((m) => m.role).lastIndexOf("assistant");

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background text-foreground">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        activeId={active?.id}
        currentTool={currentTool}
        data={data}
        query={query}
        setQuery={setQuery}
        onNewChat={() => handleNewChat()}
        onPickTool={handlePickTool}
        onPickConversation={handlePickConversation}
        onCreateProject={(name) => data.addProject(name)}
        onOpenKnowledge={openKnowledgeFor}
        onDeleteConversation={confirmAndDeleteConversation}
        onDeleteProject={confirmAndDeleteProject}
        isLockedForUser={isLockedForUser}
      />

      <main className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <div className="flex h-14 items-center justify-between border-b border-border px-5 sm:px-8">
          <div className="flex min-w-0 items-center gap-2.5">
            <ToolIcon className="h-4 w-4 shrink-0 text-primary" />
            <p data-testid="workspace-conversation-title" className="truncate text-[13.5px] font-medium text-foreground">
              {active?.title || `${TOOL_LABEL[currentTool]} · New chat`}
            </p>
            {activeProject && (
              <button
                data-testid="header-knowledge-pill"
                onClick={() => openKnowledgeFor(activeProject.id)}
                className={[
                  "ml-2 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10.5px] transition-colors",
                  activeKnowledgeCount > 0
                    ? "border-primary/30 bg-primary/10 text-primary hover:bg-primary/15"
                    : "border-border bg-card text-muted-foreground hover:text-foreground",
                ].join(" ")}
                title={
                  activeKnowledgeCount > 0
                    ? `Using ${activeProject.name} memory — ${activeKnowledgeCount} section${activeKnowledgeCount === 1 ? "" : "s"} filled`
                    : `Add memory to ${activeProject.name}`
                }
              >
                {activeKnowledgeCount > 0 ? (
                  <Sparkles className="h-3 w-3" />
                ) : (
                  <BookOpen className="h-3 w-3" />
                )}
                <span className="truncate max-w-[160px]">
                  {activeKnowledgeCount > 0 ? `${activeProject.name} memory` : `Add memory to ${activeProject.name}`}
                </span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!user && (
              <span data-testid="trial-counter" className="rounded-full border border-border bg-card px-2.5 py-1 text-[11px] text-muted-foreground">
                {trial.remaining} of {LIMIT} free left
              </span>
            )}
            {user && billing.isPro && (
              <span data-testid="pro-badge" className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-500">
                <Sparkles className="h-3 w-3" /> Pro
              </span>
            )}
            {user && !billing.isPro && billing.billing && (
              <button
                data-testid="usage-badge"
                onClick={() => navigate("/pricing")}
                title="Upgrade to Pro for unlimited messages"
                className={[
                  "rounded-full border px-2.5 py-1 text-[11px] transition-colors",
                  billing.billing.messages_remaining <= 2
                    ? "border-rose-500/30 bg-rose-500/10 text-rose-500 hover:bg-rose-500/15"
                    : "border-border bg-card text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                {billing.billing.messages_used}/{billing.billing.message_limit} this month
              </button>
            )}
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} data-testid="message-list" className="flex-1 overflow-y-auto">
          {messages.length === 0 && !loading && (
            <div className="mx-auto flex h-full max-w-2xl flex-col items-center justify-center px-6 text-center">
              <ToolIcon className="h-7 w-7 text-primary" />
              <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-foreground">
                {TOOL_LABEL[currentTool] || "AI Workspace"}
              </h2>
              <p className="mt-2 max-w-md text-[13.5px] leading-relaxed text-muted-foreground">
                {currentTool === "reply_assistant" && "Paste a client message to start. Add a one-word intent on the next turn and we'll write the reply."}
                {currentTool === "proposal_generator" && "Paste a full job post. We'll write a tailored, human-sounding proposal in your chosen style."}
                {currentTool === "client_analyzer" && "Paste a job. We'll return a structured read — skills, complexity, red flags, strategy."}
              </p>
            </div>
          )}

          {messages.map((m, i) => (
            <Message
              key={m.id} msg={m}
              canRegenerate={m.role === "assistant" && i === lastAssistantIdx && !loading}
              onRegenerate={onRegenerate}
            />
          ))}

          {loading && messages.length > 0 && messages[messages.length - 1].role === "assistant"
            && !messages[messages.length - 1].content && (
            <div className="px-5 py-5 sm:px-10">
              <div className="mx-auto flex max-w-3xl gap-4">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary"><Sparkles className="h-3.5 w-3.5" /></div>
                <div className="flex items-center gap-1.5 pt-1">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground" style={{ animationDelay: "120ms" }} />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground" style={{ animationDelay: "240ms" }} />
                </div>
              </div>
            </div>
          )}
        </div>

        <Composer
          input={input}
          setInput={setInput}
          attachments={attachments}
          setAttachments={setAttachments}
          onSend={onSend}
          onStop={onStop}
          loading={loading}
          placeholder={placeholderByTool[currentTool] || "Message…"}
        />
      </main>

      <RightPanel
        conv={active}
        settings={settings}
        onSettingChange={handleSettingChange}
        lastAnalysis={active?.tool === "client_analyzer" ? lastAssistantWithJson : null}
      />

      <SignupPromptModal open={modalOpen} onOpenChange={setModalOpen} generationsUsed={LIMIT} />

      <KnowledgeBaseSheet
        open={kbSheetOpen}
        onOpenChange={setKbSheetOpen}
        project={kbProject}
        onSave={(knowledge) => data.updateKnowledge(kbProject.id, knowledge)}
      />

      <ConfirmHost />
    </div>
  );
}
